import { Directive, HostBinding, HostListener, Input } from '@angular/core';
import { EmailComposeService } from '../../../services/email-compose.service';

/**
 * Opens the in-app compose modal (the shared right-hand-drawer ComposeEmailComponent)
 * with the given address prefilled as the recipient — a drop-in replacement for
 * `href="mailto:…"` links so email addresses stay inside the app instead of handing
 * off to Outlook/Gmail. The From account is auto-selected as usual.
 *
 * Usage (SharedModule exports it, so it's available wherever SharedModule is imported):
 *   <a [appEmailCompose]="person.email">{{ person.email }}</a>
 *
 * Optional module linking — so the sent mail is linked to a record and shows in
 * that record's Email tab (mirrors the module email launcher):
 *   <a [appEmailCompose]="person.email"
 *      [emailModuleId]="13" [emailModuleDataId]="customer.id"
 *      [emailModuleName]="'Customer'" [emailReferenceName]="customer.company_title">
 *     {{ person.email }}
 *   </a>
 */
@Directive({
  selector: '[appEmailCompose]',
  standalone: true,
})
export class EmailComposeDirective {
  /** The recipient address (goes into the compose "To" field). */
  @Input('appEmailCompose') email: string = '';

  /** Optional linking: when moduleId + moduleDataId are set, the mail links to that record. */
  @Input() emailModuleId?: number | string | null;
  @Input() emailModuleDataId?: number | string | null;
  @Input() emailModuleName?: string;
  @Input() emailReferenceName?: string;
  /** Optional subject prefill. */
  @Input() emailSubject?: string;

  @HostBinding('style.cursor') cursor = 'pointer';

  constructor(private emailCompose: EmailComposeService) {}

  @HostListener('click', ['$event'])
  onClick(event?: Event): void {
    // Never let the click navigate or bubble to a clickable parent (cards/rows),
    // which would otherwise refresh/route the page instead of opening the modal.
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.email) { return; }

    this.emailCompose.open(this.email, {
      moduleId: this.emailModuleId,
      moduleDataId: this.emailModuleDataId,
      moduleName: this.emailModuleName,
      referenceName: this.emailReferenceName,
      subject: this.emailSubject,
    });
  }
}
